import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildMemberResponse, GuildMemberListResult } from '@/lib/api';

interface UseGuildMembersReturn {
  members: GuildMemberResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
  updateRole: (userId: string, role: string) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
}

export function useGuildMembers(guildId: string | undefined): UseGuildMembersReturn {
  const [members, setMembers] = useState<GuildMemberResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!!guildId);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const fetchIdRef = useRef(0);

  const fetchMembers = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      if (!guildId) return;
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const result: GuildMemberListResult = await api.getGuildMembers(guildId, {
          limit,
          offset: newOffset,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setMembers((prev) => [...prev, ...result.members]);
        } else {
          setMembers(result.members);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        if (!append) {
          setMembers([]);
          setTotal(0);
        }
        setError(err instanceof Error ? err.message : 'Failed to load members');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [guildId],
  );

  const refresh = useCallback(() => {
    setError(null);
    fetchMembers(0, false);
  }, [fetchMembers]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchMembers(offset + limit, true);
    }
  }, [fetchMembers, loading, offset, total]);

  const updateRole = useCallback(
    async (userId: string, role: string) => {
      if (!guildId) return;
      await api.updateGuildMemberRole(guildId, userId, role);
      await refresh();
    },
    [guildId, refresh],
  );

  const removeMember = useCallback(
    async (userId: string) => {
      if (!guildId) return;
      await api.removeGuildMember(guildId, userId);
      await refresh();
    },
    [guildId, refresh],
  );

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    members,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
    updateRole,
    removeMember,
  };
}
